import { useCallback, useEffect, useRef, useState } from 'react'
import { isAbortError } from '@utils/apiUtils'

interface UseAsyncDataOptions {
  /** false 時不發請求（例如還沒拿到必要參數、或使用者無權限） */
  enabled?: boolean
  /** 失敗時的處理；預設只寫 console，不彈 toast，由呼叫端決定 */
  onError?: (error: unknown) => void
}

interface UseAsyncDataResult<T> {
  data: T | null
  loading: boolean
  error: unknown
  /** 手動重新載入（例如審核完一筆之後） */
  reload: () => void
}

/**
 * 資料載入 hook：**競態保護 + 卸載保護 + 依賴變動自動重載**。
 *
 * ⛔ 解決三個在這個專案裡重複出現的問題：
 *
 * 1. **亂序覆蓋**：連續改篩選會送出多個請求，回應順序不保證。
 *    沒有保護時，先送出的慢請求後回來，會把新結果蓋掉，
 *    畫面顯示的就不是使用者選的條件。這裡用遞增序號，只接受最新一次的結果。
 *
 * 2. **靜態資料被重複載入**：使用者清單這種與篩選無關的資料，
 *    若跟 filters 綁在同一個 useEffect，每改一次條件就重抓一次。
 *    把它獨立成一個 deps 為 `[]` 的 useAsyncData 就只會載一次。
 *
 * 3. **卸載後 setState**：切頁後回應才回來，對已卸載的元件設狀態。
 *
 * 刻意**不做**快取與重試——那需要一整套 query 客戶端，
 * 這裡只解決上述三件事，維持可預測、好除錯。
 *
 * 用法：
 * ```tsx
 * // 會跟著條件變的資料
 * const { data, loading, reload } = useAsyncData(
 *   () => approvalService.getPendingApprovals(query),
 *   [query]
 * )
 * // 只載一次的靜態資料
 * const { data: users } = useAsyncData(() => userService.getUsers(), [])
 * ```
 *
 * ⚠️ deps 請放「值」而不是物件字面量。物件每次渲染都是新的，
 *    會造成無限重載——需要組合條件時用 useMemo 包起來。
 */
export function useAsyncData<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList,
  options: UseAsyncDataOptions = {}
): UseAsyncDataResult<T> {
  const { enabled = true, onError } = options

  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<unknown>(null)

  // 存最新的 fetcher / onError，這樣它們不必是穩定引用也不會觸發重載
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  /** 只有「最新一次」的請求有資格寫入狀態 */
  const runIdRef = useRef(0)
  const mountedRef = useRef(true)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (!enabled) {
      /*
       * ⛔ 停用時要一併作廢「還在飛的請求」。
       *    只 return 而不推進 runId 的話，停用前送出的請求回來時
       *    `runId === runIdRef.current` 仍然成立，照樣會寫進 data、
       *    甚至觸發 onError——而呼叫端之所以停用，往往正是因為
       *    權限沒了或必要參數不見了，那份結果不該再被採用。
       */
      runIdRef.current++
      setLoading(false)
      return
    }

    const runId = ++runIdRef.current
    setLoading(true)

    ;(async () => {
      try {
        const result = await fetcherRef.current()
        // 過期的回應直接丟掉，不要覆蓋新結果
        if (!mountedRef.current || runId !== runIdRef.current) return
        setData(result)
        setError(null)
      } catch (err) {
        if (!mountedRef.current || runId !== runIdRef.current) return
        // 路由切換造成的 abort 不是錯誤，不要顯示成失敗
        if (isAbortError(err)) return
        setError(err)
        if (onErrorRef.current) onErrorRef.current(err)
        else console.error('[useAsyncData] 載入失敗:', err)
      } finally {
        if (mountedRef.current && runId === runIdRef.current) setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled, reloadToken])

  const reload = useCallback(() => setReloadToken((n) => n + 1), [])

  return { data, loading, error, reload }
}

export default useAsyncData
